import { Star } from 'lucide-react';
import type { StorefrontSection } from '@mlm/shared';
import { useStorefrontContent } from '../../lib/hooks';
import { storefrontAssetUrl } from '../../../lib/api';

type Cfg = Extract<StorefrontSection, { type: 'testimonials' }>['config'];

const COLS: Record<NonNullable<Cfg['cols']>, string> = {
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
};

/** Testimonial cards — reads the existing `company.testimonials` CMS block. */
export function TestimonialsSection({ section }: { section: StorefrontSection }) {
  const cfg = section.config as Cfg;
  const items = useStorefrontContent().data.company.testimonials ?? [];
  if (items.length === 0) return null;
  const cols = cfg.cols ?? 2;

  return (
    <section className="bg-muted py-14">
      <div className="mx-auto max-w-[1100px] px-4">
        <div className="text-center">
          <h2 className="font-heading text-[32px] font-extrabold text-foreground">{cfg.heading}</h2>
          {cfg.subheading && <p className="mt-2 text-sm text-muted-foreground">{cfg.subheading}</p>}
        </div>
        <div className={`mt-9 grid gap-5 ${COLS[cols]}`}>
          {items.map((t, i) => (
            <div
              key={i}
              className="rounded-2xl border border-border bg-card p-8 shadow-card transition-transform hover:-translate-y-1"
            >
              <div className="flex gap-0.5 text-gold">
                {Array.from({ length: 5 }).map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <p className="mt-3 text-[15px] italic leading-relaxed text-muted-foreground">
                “{t.quote}”
              </p>
              <div className="mt-5 flex items-center gap-3.5">
                <span className="h-12 w-12 shrink-0 overflow-hidden rounded-full bg-accent">
                  {t.avatarKey && (
                    <img
                      src={storefrontAssetUrl(t.avatarKey)}
                      alt={t.author}
                      className="h-full w-full object-cover"
                    />
                  )}
                </span>
                <div>
                  <div className="text-[15px] font-bold text-foreground">{t.author}</div>
                  {t.role && <div className="text-[13px] text-primary">{t.role}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
